// src/components/markdown-renderer/config/markdownConfig.ts
import { plantuml } from '@mdit/plugin-plantuml'
import MarkdownIt from 'markdown-it'
import markdownItAbbr from 'markdown-it-abbr'
import markdownItContainer from 'markdown-it-container'
import markdownItFootnote from 'markdown-it-footnote'
import markdownItHighlightjs from 'markdown-it-highlightjs'
import markdownItHtml5Embed from 'markdown-it-html5-embed'
import markdownItMark from 'markdown-it-mark'
import markdownItSub from 'markdown-it-sub'
import markdownItSup from 'markdown-it-sup'
import 'highlight.js/styles/github.css'

// Create Mermaid plugin for markdown-it
export const createMermaidPlugin = (contentMap?: Map<string, string>) => {
    let diagramCounter = 0; // Add a counter to ensure unique IDs

    return (md: MarkdownIt) => {
        const temp = md.renderer.rules.fence?.bind(md.renderer.rules);        // Add a custom rule to handle auto-closing mermaid blocks
        md.block.ruler.before('fence', 'mermaid_auto_close', (state, start, end, silent) => {
            const lineStart = state.bMarks[start] + state.tShift[start];
            const lineEnd = state.eMarks[start];
            const lineText = state.src.slice(lineStart, lineEnd);

            // Check if line starts with ```mermaid
            const mermaidRegex = /^```mermaid\s*$/;
            if (!mermaidRegex.exec(lineText)) {
                return false;
            }

            if (silent) return true;

            let nextLine = start + 1;
            let mermaidContent = '';
            let endLine = nextLine;            // Look for the end of the mermaid content
            while (nextLine < end) {
                const nextLineStart = state.bMarks[nextLine] + state.tShift[nextLine];
                const nextLineEnd = state.eMarks[nextLine];
                const nextLineText = state.src.slice(nextLineStart, nextLineEnd);

                // Check for explicit closing ```
                if (nextLineText.trim() === '```') {
                    endLine = nextLine + 1; // Include the closing line
                    break;
                }

                // Auto-close conditions:
                // 1. Empty line followed by content that starts at column 0
                // 2. Line that starts at column 0 and looks like markdown
                if (nextLineText.trim() === '') {
                    // Empty line - check if next non-empty line starts at beginning
                    let lookAhead = nextLine + 1;
                    while (lookAhead < end) {
                        const lookAheadStart = state.bMarks[lookAhead] + state.tShift[lookAhead];
                        const lookAheadEnd = state.eMarks[lookAhead];
                        const lookAheadText = state.src.slice(lookAheadStart, lookAheadEnd);

                        if (lookAheadText.trim() === '') {
                            lookAhead++;
                            continue;
                        }

                        // If next non-empty line starts at beginning, auto-close
                        if (state.tShift[lookAhead] === 0) {
                            endLine = nextLine; // Don't include the empty line in content
                            nextLine = end; // Exit the loop
                            break;
                        }
                        break;
                    }
                    if (nextLine >= end) break; // If we set nextLine to end, break out
                } else if (state.tShift[nextLine] === 0) {
                    // Line starts at beginning - check if it looks like markdown content
                    const markdownRegex = /^(#|\*|-|\d+\.|\[|\||>|```)/;
                    if (markdownRegex.exec(nextLineText)) {
                        endLine = nextLine; // Don't include this line in content
                        break;
                    }
                }

                // Add line to mermaid content (including empty lines to preserve structure)
                if (mermaidContent) mermaidContent += '\n';
                mermaidContent += nextLineText;
                nextLine++;
            }

            // Create the mermaid token
            const token = state.push('mermaid_diagram', 'div', 0);
            token.content = mermaidContent.trim();
            token.map = [start, endLine];

            state.line = endLine;
            return true;
        });

        // Add renderer for the custom mermaid token
        md.renderer.rules.mermaid_diagram = (tokens, idx) => {
            const token = tokens[idx];
            const code = token.content;

            try {
                //                console.log('Mermaid plugin processing auto-close code:', code);

                // Generate unique ID for the diagram using content hash, position, and counter
                let hash = 0;
                for (let i = 0; i < code.length; i++) {
                    const char = code.charCodeAt(i);
                    hash = ((hash << 5) - hash) + char;
                    hash = hash & hash; // Convert to 32bit integer
                }
                const contentHash = Math.abs(hash).toString(36).substring(0, 6);
                const diagramId = `mermaid-${contentHash}-${idx}-${++diagramCounter}`;

                //                console.log('Generated auto-close diagram ID:', diagramId);

                // Store content in the map if provided
                if (contentMap) {
                    contentMap.set(diagramId, code);
                    //                    console.log('Stored auto-close mermaid content in map for ID:', diagramId);
                }

                return `<div class="mermaid-diagram" data-diagram-id="${diagramId}" data-render-state="pending">
            <div class="mermaid-placeholder">Loading Mermaid diagram...</div>
          </div>`;
            } catch (error) {
                return `<div class="mermaid-error">Error rendering auto-close Mermaid diagram: ${error instanceof Error ? error.message : 'Unknown error'}</div>`;
            }
        };

        // Keep the original fence rule for properly closed mermaid blocks
        md.renderer.rules.fence = (tokens, idx, options, env, renderer) => {
            const token = tokens[idx];
            const code = token.content.trim();
            const info = token.info ? md.utils.unescapeAll(token.info).trim() : '';

            if (info === 'mermaid') {
                try {
                    //console.log('Mermaid plugin processing fenced code:', code);

                    // Generate unique ID for the diagram using content hash, position, and counter
                    let hash = 0;
                    for (let i = 0; i < code.length; i++) {
                        const char = code.charCodeAt(i);
                        hash = ((hash << 5) - hash) + char;
                        hash = hash & hash; // Convert to 32bit integer
                    }
                    const contentHash = Math.abs(hash).toString(36).substring(0, 6);
                    const diagramId = `mermaid-${contentHash}-${idx}-${++diagramCounter}`;

                    //console.log('Generated fenced diagram ID:', diagramId);

                    // Store content in the map if provided
                    if (contentMap) {
                        contentMap.set(diagramId, code);
                        //console.log('Stored fenced mermaid content in map for ID:', diagramId);
                    }

                    return `<div class="mermaid-diagram" data-diagram-id="${diagramId}" data-render-state="pending">
            <div class="mermaid-placeholder">Loading Mermaid diagram...</div>
          </div>`;
                } catch (error) {
                    return `<div class="mermaid-error">Error rendering fenced Mermaid diagram: ${error instanceof Error ? error.message : 'Unknown error'}</div>`;
                }
            }

            // Fall back to default fence renderer for non-mermaid code blocks
            return temp ? temp(tokens, idx, options, env, renderer) : '';
        };
    };
};

// Container configurations
const createWarningContainer = () => ({
    validate: (params: string) => /^warning$/.exec(params.trim()) !== null,
    render: (tokens: any[], idx: number) =>
        tokens[idx].nesting === 1 ? '<div className="warning">' : '</div>',
});

const createTipContainer = () => ({
    validate: (params: string) => /^tip$/.exec(params.trim()) !== null,
    render: (tokens: any[], idx: number) =>
        tokens[idx].nesting === 1 ? '<div className="tip">' : '</div>',
});

const createNoteContainer = () => ({
    validate: (params: string) => /^note$/.exec(params.trim()) !== null,
    render: (tokens: any[], idx: number) =>
        tokens[idx].nesting === 1 ? '<div className="note">' : '</div>',
});

const createSectionContainer = () => ({
    validate: (params: string) => /^section\s*(.*)$/.exec(params.trim()) !== null,
    render: (tokens: any[], idx: number) => {
        const token = tokens[idx]
        const info = /^section\s*(.*)$/.exec(token.info.trim())
        if (token.nesting === 1) {
            const title = info?.[1] ? info[1].trim() : 'Unnamed Section'
            return `<div className="styles.section" data-title="${title}">`
        } else {
            return '</div>\n'
        }
    },
});

// Initialize and configure markdown-it instance
export const createMarkdownInstance = (contentMap?: Map<string, string>): MarkdownIt => {
    const md = new MarkdownIt({
        html: true,
        linkify: true,
        typographer: true,
        breaks: true,
    })
        .use(plantuml)
        .use(markdownItHtml5Embed, {
            html5embed: {
                useImageSyntax: true,
                useLinkSyntax: true,
            },
        })
        .use(markdownItHighlightjs)
        .use(markdownItFootnote)
        .use(markdownItSup)
        .use(markdownItSub)
        .use(markdownItAbbr)
        .use(markdownItMark)
        .use(markdownItContainer, 'warning', createWarningContainer())
        .use(markdownItContainer, 'tip', createTipContainer())
        .use(markdownItContainer, 'note', createNoteContainer())
        .use(markdownItContainer, 'section', createSectionContainer())
        .use(createMermaidPlugin(contentMap));

    return md;
};

// Create singleton instance
export const markdownInstance = createMarkdownInstance();
